import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { Select } from '@ngxs/store';
import { combineLatest, map, Observable, shareReplay } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ConnectedPosition } from '@angular/cdk/overlay';
import { UserLocationsListState } from 'src/states/user-locations-list/user-locations-list.state';
import { UserLocationsListItemDto } from 'src/app/models/user-locations-list-item-dto';
import { UserService } from 'src/app/services/user.service';
import { HomeLocationState } from '../../../../states';
import { LogoutButtonComponent } from '../../../../shared/components/logout-button/logout-button.component';
import { convertUserLocationToOptionsDto } from 'src/app/utils/convert-user-location-to-options-dto.util';
import { sortUserLocations } from 'src/app/utils/sort-user-locations.util';
import {
  AutoSuggestComponent,
  AutoSuggestEndOfListDirective,
  AutoSuggestOptionDirective,
  IconComponent,
  OptionDto,
  TextTooltipDirective
} from '@bioreferencehealth/ui-kit';

@UntilDestroy()
@Component({
  selector: 'app-account-dropdown',
  standalone: true,
  imports: [
    CommonModule,
    AutoSuggestComponent,
    AutoSuggestOptionDirective,
    AutoSuggestEndOfListDirective,
    IconComponent,
    ReactiveFormsModule,
    LogoutButtonComponent,
    TextTooltipDirective
  ],
  templateUrl: './switch-account-dropdown.component.html',
  styleUrls: ['./switch-account-dropdown.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SwitchAccountDropdownComponent implements OnInit {
  private readonly userService = inject(UserService);

  public readonly control = new FormControl<OptionDto | null>(null);

  @Select(HomeLocationState.loaded) public loaded$!: Observable<boolean>;
  @Select(HomeLocationState.locationId) public locationId$!: Observable<number>;
  @Select(UserLocationsListState.userLocationsList)
  public userLocationsList$!: Observable<UserLocationsListItemDto[]>;

  public locations$: Observable<OptionDto[]> = this.userLocationsList$.pipe(
    map((userLocations: UserLocationsListItemDto[]) => {
      return sortUserLocations(userLocations).map(userLocation => convertUserLocationToOptionsDto(userLocation));
    }),
    shareReplay({ refCount: true, bufferSize: 1 })
  );

  public positions: ConnectedPosition[] = [
    {
      originX: 'end',
      originY: 'bottom',
      overlayX: 'end',
      overlayY: 'top',
      offsetX: -8
    }
  ];

  public filterOptions(option: OptionDto, searchString: string): boolean {
    const description = option.description as string | undefined;
    return (option.value.toLowerCase().includes(searchString) || description?.toLowerCase().includes(searchString)) ?? false;
  }

  public ngOnInit(): void {
    combineLatest([this.locationId$, this.locations$])
      .pipe(untilDestroyed(this))
      .subscribe(([locationId, locations]) => {
        const value = locations.find(option => option.id === locationId);
        if (value) {
          this.control.setValue(value);
        }
      });
  }

  public comparatorOptions(option: OptionDto | null, selectedOption: OptionDto | null | undefined): boolean {
    return option?.id === selectedOption?.id;
  }

  public onSelected($event: OptionDto | null): void {
    if ($event) {
      this.userService.switchHomeLocation(+$event.id);
    }
  }
}
